import type { DataTableColumn } from "@vjal/ui";
import type { CoachingRole } from "../domain/roles";
import { evaluatePermissionGrant } from "../domain/sensitive-surfaces";
import { UserEditPage } from "./users/user-edit-page";
import type { ProductScreenContract, TableRow } from "./product-route-contracts";

// ── User edit (Wave 1 — REBUILT as a focused gold-pattern page) ────────────
// Replaces the thin "contract-surface" user-edit. The /users/:id/edit route now
// renders UserEditPage: the user is read (from the users list) through
// useHonestQuery -> <HonestState>, the edit form opens inline, and Save toasts
// only after the backend confirms then navigates back. Granting the admin role
// requires super_admin (enforced in the role picker + server re-check). The
// factory below preserves the route registration + provenance metadata.

const roleTargets: CoachingRole[] = ["coach", "company_admin", "participant", "admin", "super_admin"];

export function userEditScreen(): ProductScreenContract {
  const rows = roleTargets.map((target) => {
    const decision = evaluatePermissionGrant("admin", target);
    return {
      id: target,
      targetRole: target,
      grant: decision.allowed ? "allowed" : "blocked",
      reason: decision.label
    };
  });

  return {
    title: () => "Edit user",
    primaryJob: "Update a user's allowed fields and role while making the admin/super-admin grant boundary impossible to miss.",
    secondaryJob: "Granting the admin role requires a super admin; deactivate stays a confirmation workflow.",
    workTitle: "User edit",
    rows,
    columns: grantColumns(),
    states: ["loading", "empty", "permission-denied", "unavailable", "error", "grant-blocked"],
    apiFamilies: ["user-service", "permission contracts"],
    roleDeltas: "Super admin can grant any role incl. admin; admin cannot grant admin or super_admin; non-staff are route-denied.",
    extensionSlots: ["permission templates", "scope assignment", "deactivate/reactivate confirmation"],
    mobilePosture: "Role, scope, and deactivate controls stack with blocked grant warnings visible.",
    evidenceRequirement: "Playwright must prove admin cannot grant admin/super_admin, the edit form saves, and deactivate confirms.",
    surfaceMode: "focused",
    customBody: UserEditPage
  };
}

function grantColumns(): DataTableColumn<TableRow>[] {
  return [
    { key: "targetRole", header: "Target role" },
    { key: "grant", header: "Grant" },
    { key: "reason", header: "Reason" }
  ];
}
